/**
 * `/admin/ad-engine/$videoId` — the Ad Engine debug page (spec §11 Chunk 2 + 3).
 *
 * Owns everything impure: the polled `adEngine.get` query, one mutation per §3.5 procedure, the
 * keyboard layer and the conflict toast. The view below it is controlled and pure.
 *
 * `docVersion` is CAS on every write: on `E_DOC_CONFLICT` (TRPC `CONFLICT`) the page refetches
 * and tells the operator to retry — it never replays the write over someone else's document.
 */
import { Skeleton, toast } from "@aix/ui"
import { EAdPacing, EAdStage } from "@aix/vidmax-shared/src/schemas/adProject"
import { Link, createFileRoute } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useState } from "react"

import { AdEngineWorkspace } from "~/components/AdEngine/AdEngineWorkspace"
import { BindingsStrip } from "~/components/AdEngine/BindingsStrip"
import {
  AD_ENGINE_SETUP_VIEW,
  canOpenInEditor,
  isAdEngineView,
  parseSelection,
  hasPendingWork,
  OPEN_IN_EDITOR_BLOCKED_REASON,
  pendingStagesOf,
} from "~/components/AdEngine/adEngineView"
import type { TAdEngineView, TAdTakeStage } from "~/components/AdEngine/adEngineView"
import { ShortcutsHelp } from "~/components/AdEngine/ShortcutsHelp"
import { useAdEngineShortcuts } from "~/components/AdEngine/useAdEngineShortcuts"
import { EAdInspectorMode } from "~/components/AdEngine/types"
import type {
  IAdEngineActions,
  IAdEngineData,
  IAdEngineViewState,
  IAdRunScope,
  IAdShotPatch,
} from "~/components/AdEngine/types"
import CustomHead from "~/components/SEOHead/CustomHead"
import { SuperAdminLayout } from "~/layouts/SuperAdminLayout"
import { api } from "~/utils/api"

const POLL_INTERVAL_MS = 4_000

function AdEnginePage() {
  const { videoId } = Route.useParams()
  const utils = api.useUtils()

  // `?stage=` is the source of truth for the focused section, so every stage is a shareable deep
  // link. Writes `replace`, because scrolling the page retargets `view` continuously and each of
  // those must not become a back-button stop.
  const { stage: searchStage, sel: searchSel } = Route.useSearch()
  const navigate = Route.useNavigate()
  const view = searchStage ?? AD_ENGINE_SETUP_VIEW
  const setView = useCallback(
    (next: TAdEngineView) => {
      void navigate({ search: (prev) => ({ ...prev, stage: next }), replace: true })
    },
    [navigate],
  )

  // `?sel=` too: "look at this shot" is the link worth pasting to someone, not just "this stage".
  const selection = searchSel ?? null
  const setSelection = useCallback(
    (next: string | null) => {
      void navigate({ search: (prev) => ({ ...prev, ...(next ? { sel: next } : { sel: undefined }) }), replace: true })
    },
    [navigate],
  )
  const [inspectorMode, setInspectorMode] = useState<EAdInspectorMode | null>(null)
  const [showRawJson, setShowRawJson] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [busyStage, setBusyStage] = useState<EAdStage | null>(null)

  const projectQuery = api.adEngine.get.useQuery(
    { videoId },
    {
      refetchInterval: (data) => (data && hasPendingWork(data.doc) ? POLL_INTERVAL_MS : false),
    },
  )

  const refetch = useCallback(() => {
    void utils.adEngine.get.invalidate({ videoId })
  }, [utils, videoId])

  /** One place decides what a failed mutation means; conflicts always reload, never overwrite. */
  const onMutationError = useCallback(
    (error: { message: string; data?: { code?: string } | null }) => {
      if (error.data?.code === "CONFLICT") {
        toast.error("reloaded, retry — the document changed while you were editing")
      } else {
        toast.error(error.message)
      }
      setBusyStage(null)
      refetch()
    },
    [refetch],
  )

  const onMutationSettled = useCallback(() => {
    setBusyStage(null)
    refetch()
  }, [refetch])

  const mutationOptions = { onError: onMutationError, onSuccess: onMutationSettled }

  const runStageMutation = api.adEngine.runStage.useMutation(mutationOptions)
  const pollStageMutation = api.adEngine.pollStage.useMutation(mutationOptions)
  const setOverrideMutation = api.adEngine.setOverride.useMutation(mutationOptions)
  const setNotesMutation = api.adEngine.setNotes.useMutation(mutationOptions)
  const setPacingMutation = api.adEngine.setPacing.useMutation(mutationOptions)
  const selectTakeMutation = api.adEngine.selectTake.useMutation(mutationOptions)
  const approveKeyframesMutation = api.adEngine.approveKeyframes.useMutation(mutationOptions)
  const editScriptLineMutation = api.adEngine.editScriptLine.useMutation(mutationOptions)
  const editShotMutation = api.adEngine.editShot.useMutation(mutationOptions)

  const docVersion = projectQuery.data?.doc.docVersion ?? 1
  const freshness = useMemo(() => projectQuery.data?.freshness ?? {}, [projectQuery.data?.freshness])

  const actions = useMemo<IAdEngineActions>(
    () => ({
      runStage: (stage: EAdStage, options?: { force?: boolean; scope?: IAdRunScope }) => {
        setBusyStage(stage)
        runStageMutation.mutate({
          videoId,
          stage,
          docVersion,
          ...(options?.force ? { force: true } : {}),
          ...(options?.scope ? { scope: options.scope } : {}),
        })
      },
      pollStage: (stage: EAdStage) => pollStageMutation.mutate({ videoId, stage }),
      setOverride: (stage: EAdStage, text: string | null) =>
        setOverrideMutation.mutate({ videoId, stage, text, docVersion }),
      // Chained, not fired together: the save bumps `docVersion`, so a rerun sent in the same tick
      // carries the version the save just invalidated — it either 409s or runs the old prompt.
      setOverrideAndRerun: (stage: EAdStage, text: string | null) => {
        setBusyStage(stage)
        void setOverrideMutation
          .mutateAsync({ videoId, stage, text, docVersion })
          .then((doc) => {
            setBusyStage(stage)
            return runStageMutation.mutateAsync({ videoId, stage, docVersion: doc.docVersion, force: true })
          })
          // Both mutations already surface their own failure through `onMutationError`.
          .catch(() => undefined)
      },
      setNotes: (notes: string[]) => setNotesMutation.mutate({ videoId, notes, docVersion }),
      setPacing: (pacing: EAdPacing) => setPacingMutation.mutate({ videoId, pacing, docVersion }),
      openInEditor: () => {
        // Silent no-ops were the bug: the editor renders `tracksJson`, so before TIMELINE there is
        // nothing to open — say so instead of firing a request whose result goes nowhere.
        if (!canOpenInEditor(freshness)) {
          toast.error(OPEN_IN_EDITOR_BLOCKED_REASON)
          return
        }
        utils.adEngine.openInEditor
          .fetch({ videoId })
          .then((result) => {
            if (!result?.url) {
              toast.error("could not resolve the editor URL")
              return
            }
            // A keyboard-triggered `window.open` runs after an await, so it has lost its user
            // activation and Chrome blocks the tab silently — fall back to this tab.
            if (!window.open(result.url, "_blank", "noopener")) window.location.assign(result.url)
          })
          .catch(() => toast.error("could not resolve the editor URL"))
      },
      selectTake: (stage: TAdTakeStage, id: string, takeIndex: number) =>
        selectTakeMutation.mutate({ videoId, stage, id, takeIndex, docVersion }),
      approveKeyframes: () => approveKeyframesMutation.mutate({ videoId, docVersion }),
      editScriptLine: (lineId: string, text: string) =>
        editScriptLineMutation.mutate({ videoId, lineId, text, docVersion }),
      editShot: (shotId: string, patch: IAdShotPatch) =>
        editShotMutation.mutate({ videoId, shotId, patch, docVersion }),
    }),
    [
      approveKeyframesMutation,
      docVersion,
      editScriptLineMutation,
      freshness,
      editShotMutation,
      pollStageMutation,
      runStageMutation,
      selectTakeMutation,
      setNotesMutation,
      setOverrideMutation,
      setPacingMutation,
      utils,
      videoId,
    ],
  )

  // Every write, not just `runStage` — selecting a take or approving is equally "working".
  const mutating = [
    runStageMutation,
    pollStageMutation,
    setOverrideMutation,
    setNotesMutation,
    setPacingMutation,
    selectTakeMutation,
    approveKeyframesMutation,
    editScriptLineMutation,
    editShotMutation,
  ].some((mutation) => mutation.isLoading)

  const viewState: IAdEngineViewState = {
    view,
    onViewChange: setView,
    selection,
    onSelectionChange: setSelection,
    inspectorMode,
    onInspectorModeChange: setInspectorMode,
    showRawJson,
    busyStage,
    mutating,
    refreshing: projectQuery.isFetching && !projectQuery.isLoading,
  }

  const data: IAdEngineData | null = projectQuery.data
    ? {
        doc: projectQuery.data.doc,
        freshness: projectQuery.data.freshness,
        format: projectQuery.data.format,
        mediaUrls: projectQuery.data.mediaUrls ?? {},
        bindingEntities: projectQuery.data.bindingEntities ?? {},
      }
    : null

  // `adEngine.get` only reads — `pollStage` is the one call that settles finished jobs into the
  // document. Refetching alone therefore re-renders the same pending takes forever, so the page
  // drives the poll itself instead of waiting for someone to press "⟳ poll".
  const pendingKey = (data ? pendingStagesOf(data.doc) : []).join(",")
  const pollPending = pollStageMutation.isLoading
  useEffect(() => {
    if (pendingKey.length === 0) return
    const timer = setInterval(() => {
      if (pollPending) return
      for (const stage of pendingKey.split(",") as EAdStage[]) pollStageMutation.mutate({ videoId, stage })
    }, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
    // `pollStageMutation` is a stable tRPC handle; keying on the pending set avoids a new interval
    // per render while still restarting when what is in flight changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKey, pollPending, videoId])

  useAdEngineShortcuts({
    data,
    viewState,
    actions,
    helpOpen,
    onHelpOpenChange: setHelpOpen,
    onToggleRawJson: () => setShowRawJson((value) => !value),
  })

  return (
    <SuperAdminLayout fixedViewport>
      <CustomHead title="Ad Engine" />
      {projectQuery.isLoading && <Skeleton className="m-5 h-96" />}
      {!projectQuery.isLoading && !data && (
        <p className="p-6 text-sm text-muted-foreground">
          No ad project on video <span className="font-mono">{videoId}</span> — initialise it with{" "}
          <span className="font-mono">asd-vidmax-ad-engine init</span> first.
        </p>
      )}
      {data && (
        <div className="flex h-full min-h-0 flex-col">
          <div className="min-h-0 flex-1">
            <AdEngineWorkspace
              data={data}
              viewState={viewState}
              actions={actions}
              bindings={
                <div className="flex flex-wrap items-center gap-3">
                  <Link to="/admin/ad-engine" className="text-xs text-muted-foreground hover:text-foreground">
                    ← All ad projects
                  </Link>
                  <BindingsStrip
                    bindings={data.doc.bindings}
                    bindingEntities={data.bindingEntities}
                    roles={data.format.concept.roles}
                    onSelect={setSelection}
                  />
                </div>
              }
              onToggleRawJson={() => setShowRawJson((value) => !value)}
              onOpenHelp={() => setHelpOpen(true)}
            />
          </div>
        </div>
      )}
      <ShortcutsHelp open={helpOpen} onOpenChange={setHelpOpen} />
    </SuperAdminLayout>
  )
}

interface IAdEngineSearch {
  stage?: TAdEngineView
  /** `kind:id`, validated by `parseSelection` — see `EAdSelectionKind`. */
  sel?: string
}

export const Route = createFileRoute("/admin/ad-engine/$videoId")({
  component: AdEnginePage,
  // An unknown or absent `stage` drops back to `setup` rather than rendering nothing.
  validateSearch: (search: Record<string, unknown>): IAdEngineSearch => ({
    ...(isAdEngineView(search.stage) ? { stage: search.stage } : {}),
    ...(typeof search.sel === "string" && parseSelection(search.sel) ? { sel: search.sel } : {}),
  }),
})
